import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';

interface CloseTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (closureData: {
    closedCode: string;
    causeCode: string;
    causeNotes: string;
    proposedFixNotes: string;
    workaround: string;
  }) => void;
  taskId: string;
}

const CloseTaskModal: React.FC<CloseTaskModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  taskId: _taskId
}) => {
  const [causeCode, setCauseCode] = useState<string>('');
  const [causeNotes, setCauseNotes] = useState<string>('');
  const [proposedFixNotes, setProposedFixNotes] = useState<string>('');
  const [workaround, setWorkaround] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const causeCodeOptions = [
    { value: 'environmental_disaster', label: 'Environmental Disaster' },
    { value: 'hardware_issues', label: 'Hardware Issues' },
    { value: 'people_process_documentation', label: 'People / Process / Documentation' },
    { value: 'software_issues', label: 'Software Issues' },
    { value: 'vendor_issues', label: 'Vendor Issues' },
    { value: 'none', label: 'None' }
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!causeCode) {
      newErrors.causeCode = 'Cause Code is required';
    }
    if (!causeNotes.trim()) {
      newErrors.causeNotes = 'Cause Notes is required';
    }
    if (!proposedFixNotes.trim()) {
      newErrors.proposedFixNotes = 'Proposed Fix Notes is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm({
        closedCode: 'completed',
        causeCode,
        causeNotes,
        proposedFixNotes,
        workaround
      });
      handleClose();
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCauseCode('');
    setCauseNotes('');
    setProposedFixNotes('');
    setWorkaround('');
    setErrors({});
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Close Task
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 space-y-5">
                  {/* Closed Code and Cause Code - Same row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center gap-3">
                      <label htmlFor="closedCode" className="text-sm font-medium text-gray-700 min-w-[120px] flex-shrink-0">
                        Closed Code
                      </label>
                      <input
                        type="text"
                        id="closedCode"
                        value="Completed"
                        disabled
                        className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 text-sm cursor-not-allowed"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label htmlFor="causeCode" className="text-sm font-medium text-gray-700 min-w-[120px] flex-shrink-0">
                        Cause Code <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="causeCode"
                        value={causeCode}
                        onChange={(e) => {
                          setCauseCode(e.target.value);
                          setErrors({ ...errors, causeCode: '' });
                        }}
                        className={`flex-1 rounded-md shadow-sm py-2.5 px-3 text-sm ${
                          errors.causeCode
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                      >
                        <option value="">Select Cause Code</option>
                        {causeCodeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {errors.causeCode && (
                    <p className="mt-1 text-xs text-red-600 ml-36">{errors.causeCode}</p>
                  )}

                  {/* Cause Notes - Full width */}
                  <div className="flex items-start gap-4">
                    <label htmlFor="causeNotes" className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2.5">
                      Cause Notes <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1">
                      <RichTextEditor
                        value={causeNotes}
                        onChange={(value) => {
                          setCauseNotes(value);
                          setErrors({ ...errors, causeNotes: '' });
                        }}
                        placeholder="Enter the cause notes..."
                        minHeight="120px"
                      />
                    </div>
                  </div>
                  {errors.causeNotes && (
                    <p className="mt-1 text-xs text-red-600 ml-36">{errors.causeNotes}</p>
                  )}

                  {/* Proposed Fix Notes - Full width */}
                  <div className="flex items-start gap-4">
                    <label htmlFor="proposedFixNotes" className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2.5">
                      Proposed Fix Notes <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1">
                      <RichTextEditor
                        value={proposedFixNotes}
                        onChange={(value) => {
                          setProposedFixNotes(value);
                          setErrors({ ...errors, proposedFixNotes: '' });
                        }}
                        placeholder="Enter the proposed fix notes..."
                        minHeight="120px"
                      />
                    </div>
                  </div>
                  {errors.proposedFixNotes && (
                    <p className="mt-1 text-xs text-red-600 ml-36">{errors.proposedFixNotes}</p>
                  )}

                  {/* Workaround - Full width */}
                  <div className="flex items-start gap-4">
                    <label htmlFor="workaround" className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2.5">
                      Workaround
                    </label>
                    <div className="flex-1">
                      <RichTextEditor
                        value={workaround}
                        onChange={(value) => setWorkaround(value)}
                        placeholder="Enter workaround (optional)..."
                        minHeight="120px"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Processing...' : 'Close Task'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CloseTaskModal;
